import { useCallback, useState } from 'react';
import cl from 'classnames';
import size from 'lodash/size';
import take from 'lodash/take';

import {
  CategoryID,
  CategoryLocalizedName
} from '../../../../../../categories/categoriesTypes';
import { IdFilterType } from '../../../../../../../types';
import {
  ChangeProductsFiltersFunc,
  FetchProductsFilters
} from '../../../../../../products/productsTypes';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { useProductsFilterCategories } from './hooks/useProductsFilterCategories';

import { ThreeDStockProductsFiltersCategoriesFilterItem } from './components/ThreeDStockProductsFiltersCategoriesFilterItem';

import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { Translate } from '../../../../../../../helpers/Translate';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';
import { LoadingSkeleton } from '../../../../../../../helpers/LoadingSkeleton';

import { categoriesKeys, words } from '../../../../../../../locales/keys';

const INITIAL_CATEGORIES_COUNT = 6;

interface ThreeDStockProductsFiltersCategoriesFilterProps {
  productsFilters: FetchProductsFilters;
  defaultProductCategoryId?: IdFilterType;
  changeProductsFilters: ChangeProductsFiltersFunc;
  checkedCategories: { id: CategoryID; localizedName: CategoryLocalizedName }[];
  setCheckedCategories: (
    checkedCategories: {
      id: CategoryID;
      localizedName: CategoryLocalizedName;
    }[]
  ) => void;
  changeCategory: (
    changeFilters?: FetchProductsFilters,
    removeFilters?: string[]
  ) => void;
  isCompact?: boolean;
  withoutCategoryDrilldown?: boolean;
}

function ThreeDStockProductsFiltersCategoriesFilter({
  changeProductsFilters,
  productsFilters,
  defaultProductCategoryId,
  checkedCategories,
  setCheckedCategories,
  changeCategory,
  isCompact,
  withoutCategoryDrilldown
}: ThreeDStockProductsFiltersCategoriesFilterProps) {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [minimized, setMinimized] = useState<boolean>(true);

  const toggleIsOpen = useCallback<() => void>(
    () => setIsOpen((prevState) => !prevState),
    []
  );

  const toggleMinimize = useCallback<() => void>(() => {
    setMinimized((prevState) => !prevState);
  }, [setMinimized]);

  const {
    checkedCategoryIds,
    handleCheckedCategory,
    handleChangeCategory,
    listCategories,
    listCategoriesFetched
  } = useProductsFilterCategories({
    checkedCategories,
    setCheckedCategories,
    changeProductsFilters,
    changeCategory,
    productsFilters,
    defaultProductCategoryId
  });

  if (listCategoriesFetched && size(listCategories) === 0) {
    return null;
  }

  return (
    <LoadingSkeleton loaded={listCategoriesFetched}>
      <div className={cl(isCompact ? 'p-2' : 'p-4')}>
        <div className={cl('flex flex-col', isCompact ? 'gap-1.5' : 'gap-3')}>
          <ButtonHelper
            className="flex items-center gap-2 rounded-sm"
            dataGa="categories-filter"
            onClick={toggleIsOpen}
          >
            <span
              className={cl(
                'font-semibold text-left flex-1',
                isCompact ? 'text-xs' : 'text-sm'
              )}
            >
              <Translate id={categoriesKeys.plural} />
            </span>

            <Icon
              icon={
                isOpen ? IconsEnum.CHEVRON_DOWN_SOLID : IconsEnum.CHEVRON_UP
              }
              className={cl('text-gray-500', isCompact ? 'h-4 w-4' : 'h-5 w-5')}
            />
          </ButtonHelper>

          {isOpen && (
            <ul>
              {(minimized
                ? take(listCategories, INITIAL_CATEGORIES_COUNT)
                : listCategories
              )?.map((category, index) => (
                <ThreeDStockProductsFiltersCategoriesFilterItem
                  key={index}
                  checked={checkedCategoryIds?.includes(category.id)}
                  onCheckedCategory={handleCheckedCategory}
                  onChangeCategory={handleChangeCategory}
                  category={category}
                  isCompact={isCompact}
                  withoutCategoryDrilldown={withoutCategoryDrilldown}
                />
              ))}

              {size(listCategories) > INITIAL_CATEGORIES_COUNT && (
                <PureIconButtonHelper
                  className="p-0 space-x-1 flex gap-1 text-gray-900 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-100 leading-5 focus:ring-base text-current hover:underline transition-colors duration-150 ease-out"
                  i18nText={minimized ? words.showAll : words.showLess}
                  i18nTextClassName={cl(
                    isCompact ? 'text-xs' : 'text-sm',
                    'underline'
                  )}
                  onClick={toggleMinimize}
                  icon={
                    minimized
                      ? IconsEnum.CHEVRON_DOWN_SOLID
                      : IconsEnum.CHEVRON_UP
                  }
                  iconClassName={isCompact ? 'w-4 h-4' : 'w-5 h-5'}
                />
              )}
            </ul>
          )}
        </div>
      </div>
    </LoadingSkeleton>
  );
}

export default ThreeDStockProductsFiltersCategoriesFilter;
